import React from 'react';
import {
    FlexDiv,
    Heading2,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    StatusBadge,
    Tag,
    palette,
} from '@awarego-ui/components';
import { Alert, LinearProgress } from '@mui/material';

const LEVEL_COLOR = {
    Critical: palette.vibrant.raspberry,
    High: palette.vibrant.red,
    Medium: palette.vibrant.gold,
    Low: palette.evergreen.aspargus,
};

function ExposureBar({ score }) {
    const value = Math.max(0, Math.min(100, Number(score) || 0));
    const barColor =
        value > 75
            ? LEVEL_COLOR.Critical
            : value > 50
              ? LEVEL_COLOR.High
              : value > 25
                ? LEVEL_COLOR.Medium
                : LEVEL_COLOR.Low;
    return (
        <FlexDiv column gap={4} style={{ width: '100%' }}>
            <FlexDiv style={{ justifyContent: 'space-between' }}>
                <TextTiny color={palette.graphite.charcoal}>
                    Exposure score
                </TextTiny>
                <TextTiny color={palette.graphite.heavyMetal}>
                    <strong>{value}</strong> / 100
                </TextTiny>
            </FlexDiv>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: palette.graphite.gray,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: barColor,
                    },
                }}
            />
        </FlexDiv>
    );
}

export default function AwareExposeProfile({
    person,
    profile,
    loadingAttacks,
    onGenerateAttacks,
    error,
    warnings,
}) {
    const signals = Array.isArray(profile.signals) ? profile.signals : [];
    const breakdown = Array.isArray(profile.exposure_breakdown)
        ? profile.exposure_breakdown
        : [];
    const personFirst =
        (person && person.name && person.name.split(' ')[0]) || 'Your';
    const levelColor =
        LEVEL_COLOR[profile.exposure_level] || LEVEL_COLOR.Medium;

    return (
        <div style={{ width: '100%' }}>
            <FlexDiv column gap={6} mb={24}>
                <TextTiny
                    color={palette.evergreen.aspargus}
                    style={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        display: 'block',
                    }}
                >
                    Step 1 of 2 — Your exposure
                </TextTiny>
                <Heading2 displayBlock>
                    {personFirst}&rsquo;s exposure profile
                </Heading2>
                <TextDefault color={palette.graphite.charcoal}>
                    Here is what an attacker finds about{' '}
                    {(person && person.name) || 'you'} using only public
                    sources. Your exposure score determines a large part of
                    your AwareExpose risk profile.
                </TextDefault>
            </FlexDiv>

            <FlexDiv
                alignCenter
                gap={16}
                padding={20}
                borderRadius={12}
                mb={24}
                backgroundColor={palette.pastel.red}
                style={{ border: `1px solid ${palette.pastel.red}` }}
            >
                <ExposureBar score={profile.exposure_score} />
                <StatusBadge
                    text={profile.exposure_level || 'Medium'}
                    color={levelColor}
                />
            </FlexDiv>

            <FlexDiv
                column
                gap={12}
                padding={20}
                borderRadius={12}
                mb={24}
                backgroundColor={palette.graphite.white}
                style={{ border: `1px solid ${palette.graphite.gray}` }}
            >
                <Heading3 displayBlock>
                    {profile.signals_count || signals.length} public signals
                    found
                </Heading3>
                <FlexDiv style={{ flexWrap: 'wrap', gap: 8 }}>
                    {signals.map((s, i) => {
                        const detail =
                            typeof s === 'string'
                                ? s
                                : s &&
                                  (s.text ||
                                      s.finding ||
                                      s.detail ||
                                      s.description ||
                                      s.value ||
                                      '');
                        const source = typeof s === 'object' && s ? s.source : null;
                        const text = source ? `${source}: ${detail}` : detail;
                        if (!detail) return null;
                        return (
                            <FlexDiv
                                key={i}
                                py={6}
                                px={10}
                                borderRadius={4}
                                backgroundColor={palette.evergreen.mintLight}
                                style={{
                                    border: `1px solid ${palette.evergreen.mint}`,
                                }}
                            >
                                <TextTiny color={palette.graphite.heavyMetal}>
                                    {text}
                                </TextTiny>
                            </FlexDiv>
                        );
                    })}
                </FlexDiv>
            </FlexDiv>

            {breakdown.length > 0 && (
                <FlexDiv
                    column
                    gap={4}
                    padding={20}
                    borderRadius={12}
                    mb={24}
                    backgroundColor={palette.graphite.white}
                    style={{
                        border: `1px dashed ${palette.evergreen.mint}`,
                    }}
                >
                    <Heading3 displayBlock>Exposure score breakdown</Heading3>
                    {breakdown.map((b, i) => (
                        <FlexDiv
                            key={i}
                            gap={12}
                            py={8}
                            style={{
                                alignItems: 'flex-start',
                                borderBottom:
                                    i === breakdown.length - 1
                                        ? 'none'
                                        : `1px solid ${palette.graphite.gray}`,
                            }}
                        >
                            <FlexDiv
                                column
                                gap={2}
                                style={{ flex: 1, minWidth: 0 }}
                            >
                                <TextDefault
                                    bold
                                    color={palette.graphite.heavyMetal}
                                    style={{ display: 'block' }}
                                >
                                    {b.factor}
                                </TextDefault>
                                <TextTiny
                                    color={palette.graphite.charcoal}
                                    style={{ display: 'block' }}
                                >
                                    {b.explanation}
                                </TextTiny>
                            </FlexDiv>
                            <Tag
                                text={`+${b.weight}`}
                                color={palette.vibrant.gold}
                                backgroundColor={palette.pastel.yellow}
                            />
                        </FlexDiv>
                    ))}
                </FlexDiv>
            )}

            {profile.summary && (
                <FlexDiv
                    column
                    gap={8}
                    padding={20}
                    borderRadius={12}
                    mb={24}
                    backgroundColor={palette.pastel.red}
                    style={{
                        border: `1px solid ${palette.pastel.red}`,
                    }}
                >
                    <Heading3 displayBlock>
                        What an attacker now knows
                    </Heading3>
                    <TextDefault color={palette.graphite.heavyMetal}>
                        {profile.summary}
                    </TextDefault>
                    {profile.attacker_takeaway && (
                        <TextTiny
                            color={palette.graphite.charcoal}
                            style={{ display: 'block' }}
                        >
                            {profile.attacker_takeaway}
                        </TextTiny>
                    )}
                </FlexDiv>
            )}

            {error && (
                <Alert severity="error" style={{ marginBottom: 16 }}>
                    {error}
                </Alert>
            )}
            {Array.isArray(warnings) && warnings.length > 0 && (
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                    {warnings.map((w, i) => (
                        <div key={i}>{w}</div>
                    ))}
                </Alert>
            )}

            {onGenerateAttacks && (
                <FlexDiv mt={8}>
                    <ThemedButton
                        mode="primary"
                        text={
                            loadingAttacks
                                ? 'Generating attacks…'
                                : 'Next: review your personalised attack emails →'
                        }
                        $saving={!!loadingAttacks}
                        onClick={onGenerateAttacks}
                        disabled={!!loadingAttacks}
                    />
                </FlexDiv>
            )}
        </div>
    );
}
